import type {RecipientDeliveryResponse} from "../../api/transferApi";

/**
 * Turns the per-recipient outcomes of a send into the ONE thing the sender is told
 * (kitecorp/transferify-api-service#233, kitecorp/transferify-ui#369).
 *
 * Both delivery chokepoints used to return `void`, so a send whose recipient had opted out of
 * recipient-facing mail was reported to the sender as a plain success — the defect #233 exists to fix.
 * A single send can hit BOTH chokepoints (direct emails and saved groups are combinable, ui#210), so
 * the sender's verdict has to be computed across every call the send made, not per call: one channel
 * answering DELIVERED while the other answers SUPPRESSED is a PARTIAL send, and neither response
 * alone says so.
 *
 * This module holds no React and no copy — it decides WHAT happened; the catalog says it. Keeping the
 * rule here is what lets the same verdict drive the post-upload screen and the post-hoc
 * "email instead" send without either one re-deriving it.
 */

/** What the sender is told after a send. */
export type DeliveryNoticeKind =
	/** This send contacted no provider (every channel was already completed) — say nothing new. */
	| "quiet"
	/** Every recipient was mailed — today's plain success copy. */
	| "delivered"
	/** Some recipients were mailed and some were not. */
	| "partial"
	/** Nobody was mailed. */
	| "noneReached";

export interface DeliveryNotice {
	kind: DeliveryNoticeKind;
	/** Mailboxes the provider accepted, summed across every channel of this send. */
	deliveredCount: number;
	/** Mailboxes NOT reached — opted out, or refused by the provider — summed across every channel. */
	unreachedCount: number;
	/**
	 * Mailboxes the provider REFUSED (bounce/transport), summed across every channel — the part of
	 * {@link unreachedCount} that is NOT an opt-out. Kept separate because it drives its own copy: a
	 * refusal is a transport fact, not the recipient's own choice, and the api attaches no address to
	 * it, so it can only ever be surfaced as a COUNT — never named, unlike {@link suppressedRecipients}.
	 */
	failedCount: number;
	/**
	 * The opted-out addresses, first-seen order, de-duplicated across channels. Only ever addresses
	 * the SENDER supplied (the api bounds the response that way), so naming them discloses nothing
	 * they do not already hold. Provider-refused addresses are deliberately not echoed by the api and
	 * so can never appear here — they are counted in {@link unreachedCount} and {@link failedCount}.
	 */
	suppressedRecipients: string[];
}

/**
 * The verdict for a whole send, from every chokepoint response it produced (pass one response for a
 * single-channel send, both for a combined direct + group send, none when nothing was attempted).
 *
 * Classified from the summed COUNTS rather than by combining the four `result` enums: the counts are
 * the only thing that stays meaningful once two channels are merged. On a single response the two
 * agree exactly — `DELIVERED` has nothing unreached, `SUPPRESSED` delivered nothing,
 * `ALREADY_COMPLETED` is all zeroes, `PARTIAL` is the remainder — which is why there is no separate
 * enum path to keep in sync.
 */
export function summarizeDelivery(responses: readonly RecipientDeliveryResponse[]): DeliveryNotice {
	let deliveredCount = 0;
	let unreachedCount = 0;
	let failedCount = 0;
	const suppressedRecipients: string[] = [];
	for (const response of responses) {
		// An api predating api-service#233 answers these chokepoints with an EMPTY body, and the ui
		// deploys independently of the api — so during a rolling deploy this runs against one. Skipping
		// a bodyless response degrades to exactly the pre-#233 confirmation ("sent") instead of throwing
		// on a send that actually succeeded.
		if (!isDeliveryResponse(response)) {
			continue;
		}
		deliveredCount += response.deliveredCount;
		unreachedCount += response.suppressedCount + response.failedCount;
		failedCount += response.failedCount;
		for (const recipient of response.suppressedRecipients) {
			// A recipient reachable through both a direct address and a group must be named once.
			if (!suppressedRecipients.includes(recipient)) {
				suppressedRecipients.push(recipient);
			}
		}
	}
	return {
		kind: kindOf(deliveredCount, unreachedCount),
		deliveredCount,
		unreachedCount,
		failedCount,
		suppressedRecipients,
	};
}

/** Whether this response actually carries the api-service#233 outcome, rather than a legacy empty body. */
function isDeliveryResponse(response: RecipientDeliveryResponse | undefined): boolean {
	return typeof response === "object" && response !== null
		&& typeof response.deliveredCount === "number"
		&& typeof response.suppressedCount === "number"
		&& typeof response.failedCount === "number"
		&& Array.isArray(response.suppressedRecipients);
}

function kindOf(deliveredCount: number, unreachedCount: number): DeliveryNoticeKind {
	if (deliveredCount === 0 && unreachedCount === 0) {
		return "quiet";
	}
	if (unreachedCount === 0) {
		return "delivered";
	}
	return deliveredCount === 0 ? "noneReached" : "partial";
}

/** The catalog slice this module needs; both locales supply it under `sender.success.delivery`. */
export interface DeliveryNoticeCopy {
	/** Some delivered, and every unreached one OPTED OUT — names the addresses the api echoed. */
	partial: (deliveredCount: number, skipped: string[]) => string;
	/** Nobody delivered, and every unreached one OPTED OUT — names the addresses the api echoed. */
	noneReached: (skipped: string[]) => string;
	/** Some delivered, and every unreached one was PROVIDER-REFUSED — a bare count, no addresses. */
	partialFailed: (deliveredCount: number, failedCount: number) => string;
	/** Some delivered; some opted out (named) AND some were provider-refused (counted) in one send. */
	partialSkippedAndFailed: (deliveredCount: number, skipped: string[], failedCount: number) => string;
}

/**
 * The sentence to add to the confirmation, or `null` to keep today's plain-success copy.
 *
 * `null` covers only the outcomes with genuinely nothing to add (`delivered`, `quiet`). A send that
 * reached some recipients but not others always says so — and it says WHY, because opt-out and
 * provider-refusal are different facts the sender acts on differently: opted-out addresses are NAMED
 * (the api echoes them, and it is the recipient's own choice), while provider refusals are surfaced
 * as a COUNT (the api attaches no address to a bounce). Both can be true of a single send, so the two
 * are stated side by side rather than one masking the other (kitecorp/transferify-ui#379).
 *
 * A refusal never reaches the `noneReached` branch: the api only ever reports one on a `PARTIAL`
 * result — a send that delivered to somebody (see {@link RecipientDeliveryResult}) — so a send that
 * reached no one is always an all-opted-out one, with addresses to name and no failure count to add.
 */
export function deliveryNoticeMessage(notice: DeliveryNotice, copy: DeliveryNoticeCopy): string | null {
	const skipped = notice.suppressedRecipients;
	const failedCount = notice.failedCount;
	if (skipped.length === 0 && failedCount === 0) {
		return null;
	}
	if (notice.kind === "noneReached") {
		return copy.noneReached(skipped);
	}
	if (skipped.length > 0 && failedCount > 0) {
		return copy.partialSkippedAndFailed(notice.deliveredCount, skipped, failedCount);
	}
	if (failedCount > 0) {
		return copy.partialFailed(notice.deliveredCount, failedCount);
	}
	return copy.partial(notice.deliveredCount, skipped);
}
